import {
  APICallState,
  Selectors,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useSelector} from 'Provider';
import Cookies from 'js-cookie';

import {isWebViewSelector} from 'store/content';

export enum AdobeEvent {
  pageLoad = 'pageload',
  click = 'click',
}

interface DataLayerEvent {
  event: AdobeEvent;
  [eventData: string]: any;
}

/**
 * Reads member data directly from cookies as a fallback when the Redux store
 * hasn't been hydrated yet (e.g., immediately after sign-in redirect).
 */
const getMemberDataFromCookies = () => {
  const accountId = Cookies.get('accountId') || '';
  const userInfoCookie = Cookies.get('userInfoCookie') || '';
  const tierLevel = userInfoCookie ? userInfoCookie.split('X##X')[1] || '' : '';
  return {crNumber: accountId, guestTierLevel: tierLevel};
};

/**
 * Custom hook to track Adobe events.
 * IMPORTANT: this is different than utils/trackEvent which adds to window.dataLayer
 * whereas this one adds to window.adobeDataLayer
 * @returns a function that takes the event, adds the global object and tracks the event
 */
export const useTrackEvent = () => {
  const loggedIn = useWidgetState(Selectors.isLoggedInSelector) ? '1' : '0';
  const reduxTierLevel =
    useWidgetState(Selectors.userExtendedTierCodeSelector) || '';
  const reduxCrNumber = useWidgetState(Selectors.userAccountIdSelector) || '';
  const isWebView = useSelector(isWebViewSelector);

  // Fall back to cookies when Redux store hasn't been hydrated yet.
  // This ensures member data is available immediately after sign-in redirect,
  // before the getGuestProfile API call populates the Redux store.
  const cookieData = !reduxCrNumber ? getMemberDataFromCookies() : null;
  const crNumber = reduxCrNumber || cookieData?.crNumber || '';
  const guestTierLevel = reduxTierLevel || cookieData?.guestTierLevel || '';

  /*
    Adding MFA data for every event if Status is available
    Don't need to show mfa data on the Offers screen, only Profile
  */
  const optinStatusState = useWidgetState(
    Selectors.mfaOptinStatusStateSelector
  ) as APICallState;
  const tokenValidState = useWidgetState(
    Selectors.mfaTokenValidationStateSelector
  ) as APICallState;

  const optinStatus = useWidgetState(
    Selectors.mfaOptinStatusSelector
  ) as boolean;
  const tokenValid = useWidgetState(Selectors.mfaTokenValidSelector) as boolean;

  const mfa = {
    ...([APICallState.SUCCESS, APICallState.ERROR].includes(
      optinStatusState
    ) && {optedIn: optinStatus ? '1' : '0'}),
    ...([APICallState.SUCCESS, APICallState.ERROR].includes(
      tokenValidState
    ) && {verified: tokenValid ? '1' : '0'}),
  };

  /** member and data are global meaning they will be added to every event */
  const member = {loggedIn, crNumber, guestTierLevel};
  const data = {app: isWebView ? '1' : '0'};

  const trackAdobeEvent = (event: DataLayerEvent) => {
    if (window.adobeDataLayer === undefined) {
      window.adobeDataLayer = [];
    }

    if (event) {
      window.adobeDataLayer.push({
        ...event,
        member,
        ...(mfa.optedIn || mfa.verified ? {mfa} : undefined),
        data,
      });
    }
  };

  return {trackAdobeEvent};
};
